'use client';

import type { Table } from '@tanstack/react-table';
import { Trash2, Download, Repeat } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import {
  DataTableActionBar,
  DataTableActionBarAction,
  DataTableActionBarSelection,
} from '@/components/data-table/data-table-action-bar';
import { CreateSubscriptionDialog } from '@/components/subscriptions/create-subscription-dialog';
import { PERMISSIONS, usePerm } from '@/hooks/use-perm';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Separator } from '@/components/ui/separator';
import { Order } from '@tagadapay/crm-entities';

interface OrdersTableActionBarProps {
  table: Table<Order.Order>;
  onDelete?: (orderIds: string[]) => void;
  onExport?: () => void;
  isDeleting?: boolean;
}

export function OrdersTableActionBar({
  table,
  onDelete,
  onExport,
  isDeleting = false,
}: OrdersTableActionBarProps) {
  const { t } = useTranslation('orders');
  const { can } = usePerm();
  const rows = table.getFilteredSelectedRowModel().rows;
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [showCreateSubscription, setShowCreateSubscription] = React.useState(false);

  const selectedOrderIds = rows.map((row) => row.original.id);

  // Only paid orders carry the succeeded payment a subscription rebills against, and the
  // dialog picks one price from one store — so a selection spanning stores is not offered.
  const subscribableRows = rows.filter((row) => row.original.status === 'paid' && row.original.store?.id);
  const subscribableStoreIds = Array.from(
    new Set(subscribableRows.map((row) => row.original.store.id as string)),
  );
  const canCreateSubscription =
    can(PERMISSIONS.SUBSCRIPTIONS_MANAGE) &&
    subscribableRows.length > 0 &&
    subscribableStoreIds.length === 1;

  const handleExport = React.useCallback(() => {
    if (onExport) {
      onExport();
    }
  }, [onExport]);

  const handleDeleteClick = React.useCallback(() => {
    setShowDeleteDialog(true);
  }, []);

  const handleDeleteConfirm = React.useCallback(() => {
    if (onDelete) {
      onDelete(selectedOrderIds);
      setShowDeleteDialog(false);
      table.resetRowSelection();
    }
  }, [onDelete, selectedOrderIds, table]);

  return (
    <>
      <DataTableActionBar table={table} visible={rows.length > 0}>
        <DataTableActionBarSelection table={table} />
        <Separator orientation="vertical" className="hidden data-[orientation=vertical]:h-5 sm:block" />
        <div className="flex items-center gap-1.5">
          {canCreateSubscription && (
            <DataTableActionBarAction
              tooltip={t('createSubscriptionTooltip', { count: subscribableRows.length })}
              onClick={() => setShowCreateSubscription(true)}
            >
              <Repeat className="mr-2 h-4 w-4" />
              {t('createSubscription')}
            </DataTableActionBarAction>
          )}

          {onExport && (
            <DataTableActionBarAction tooltip="Export selected" onClick={handleExport}>
              <Download className="mr-2 h-4 w-4" />
              Export
            </DataTableActionBarAction>
          )}

          {onDelete && (
            <DataTableActionBarAction
              tooltip="Delete selected"
              isPending={isDeleting}
              onClick={handleDeleteClick}
              variant="destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DataTableActionBarAction>
          )}
        </div>
      </DataTableActionBar>

      {canCreateSubscription && (
        <CreateSubscriptionDialog
          orderIds={subscribableRows.map((row) => row.original.id)}
          storeId={subscribableStoreIds[0]}
          open={showCreateSubscription}
          onOpenChange={setShowCreateSubscription}
        />
      )}

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {rows.length} order(s). This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
